import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import { RolePill, type UserRole } from '../components/RolePill'
import { MagicLinkTimeline, type MagicLinkRecord } from '../components/MagicLinkTimeline'
import { Icon } from '../components/Icon'
import type { AdminUser, AdminUserDetail, AdminUserPatch } from '../types'

// AdminUsers — spec 44. People directory + per-user detail drawer with
// magic-link timeline + inline edit of role_label and has_financial.
//
// Still deferred to a follow-up (need migrations first):
//   - 5-tier role enum on dbo.Users — currently derived from is_admin /
//     has_financial / is_volunteer in deriveRole() below
//   - Manual magic-link revoke (POST /api/access/magic-links/:id/revoke)
//     needs revoked_at + invalidated_reason columns on AuthMagicLinks
//   - suspended flag — needs `suspended_at` column on dbo.Users
//   - Self-lockout / two-admin invariant — enforced once role editing
//     covers admin demotion
// Surfaced in SNAGS.md.

type Filter = 'all' | UserRole

export function AdminUsers() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-users'],
    queryFn: ApiClient.adminUsers
  })
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<Filter>('all')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [flash, setFlash] = useState<{ kind: 'success' | 'info'; text: string } | null>(null)

  // Auto-dismiss the flash banner after a short window so the page
  // doesn't carry a stale "Added X" message forever.
  useEffect(() => {
    if (!flash) return
    const t = window.setTimeout(() => setFlash(null), 4000)
    return () => window.clearTimeout(t)
  }, [flash])

  const filtered = useMemo(() => {
    if (!data) return []
    const q = search.trim().toLowerCase()
    return data.filter((u) => {
      if (roleFilter !== 'all' && deriveRole(u) !== roleFilter) return false
      if (!q) return true
      return (
        (u.name?.toLowerCase().includes(q) ?? false) ||
        (u.upn?.toLowerCase().includes(q) ?? false) ||
        (u.email?.toLowerCase().includes(q) ?? false)
      )
    })
  }, [data, search, roleFilter])

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl mb-1">Users &amp; permissions</h1>
          <p className="text-ink-soft">
            Everyone with access to NYMR Events. Authenticated via Microsoft
            Entra SSO; click a row for sign-in history and magic-link activity.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90"
        >
          <Icon name="plus" size={14} />
          Add user
        </button>
      </div>

      {flash && (
        <div
          className={
            'rounded p-3 text-sm border ' +
            (flash.kind === 'success'
              ? 'bg-green/10 border-green text-green'
              : 'bg-parchment border-line text-ink')
          }
          role="status"
          aria-live="polite"
        >
          {flash.text}
        </div>
      )}

      {/* SSO banner — spec 44 §2 */}
      <div
        className="rounded p-3 flex items-center gap-3 text-sm"
        style={{ background: 'linear-gradient(135deg, #e0e7ec 0%, #f0f5fb 100%)' }}
      >
        <Icon name="shield-check" size={16} className="text-slate" />
        <div className="flex-1">
          <div className="font-semibold">Microsoft Entra SSO active</div>
          <div className="text-xs text-ink-soft">
            Group membership (admin, financial, volunteer) is managed in
            Entra. This view reflects what the upsert saw on each user's
            most recent sign-in.
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap bg-white border border-line rounded px-4 py-2.5">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, UPN, or email…"
          className="px-3 py-1.5 text-sm bg-white border border-line rounded outline-none focus:border-green flex-1 min-w-[16rem]"
        />
        <label className="flex items-center gap-2 text-xs">
          <span className="font-semibold uppercase tracking-wider text-ink-soft">Role</span>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as Filter)}
            className="border border-line rounded px-2 py-1 text-sm bg-white focus:outline-none focus:border-green"
          >
            <option value="all">All roles</option>
            <option value="admin">Admin</option>
            <option value="finance">Finance</option>
            <option value="manager">Manager</option>
            <option value="editor">Editor</option>
            <option value="volunteer">Volunteer</option>
          </select>
        </label>
        <div className="text-xs text-ink-soft font-mono">
          {filtered.length} of {data?.length ?? 0}
        </div>
      </div>

      {isLoading && <div className="text-ink-soft text-sm font-mono">Loading users…</div>}

      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load users</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      )}

      {data && (
        <div className="bg-white border border-line rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-ink-soft border-b border-line bg-parchment/60">
                <th className="px-4 py-2">Person</th>
                <th className="px-3 py-2 w-28">Role</th>
                <th className="px-3 py-2 w-32">Financial</th>
                <th className="px-3 py-2 w-32">Last sign-in</th>
                <th className="px-3 py-2 w-24">Links 30d</th>
                <th className="px-3 py-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  onClick={() => setSelectedId(u.id)}
                />
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-sm italic text-ink-soft">
                    No users match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {selectedId != null && (
        <UserDetailDrawer
          personId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}

      {addOpen && (
        <AddUserModal
          onClose={() => setAddOpen(false)}
          onAdded={(user) => {
            setAddOpen(false)
            setFlash({ kind: 'success', text: `Added ${user.name ?? user.upn ?? 'user'}.` })
            setSelectedId(user.id)
          }}
          onAlreadyExists={(user) => {
            setAddOpen(false)
            setFlash({
              kind: 'info',
              text: user?.name
                ? `${user.name} is already in the system.`
                : 'That user is already in the system.'
            })
            if (user) setSelectedId(user.id)
          }}
        />
      )}
    </div>
  )
}

// ---- Add user modal -----------------------------------------------------

interface AddUserModalProps {
  onClose: () => void
  onAdded: (user: AdminUser) => void
  onAlreadyExists: (user: AdminUser | null) => void
}

function AddUserModal({ onClose, onAdded, onAlreadyExists }: AddUserModalProps) {
  const qc = useQueryClient()
  const [email, setEmail] = useState('')

  const mut = useMutation({
    mutationFn: (e: string) => ApiClient.createUserFromEntra(e),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] })
      if ('error' in result && result.error === 'already-exists') {
        onAlreadyExists(result.user)
      } else if ('user' in result && result.user) {
        onAdded(result.user)
      }
    }
  })

  const trimmed = email.trim()
  const looksLikeNymr = /@nymr\.co\.uk$/i.test(trimmed)
  // Empty input shouldn't trip the "must end @nymr.co.uk" warning - only
  // flag once they've typed an @ to avoid heckling on every keystroke.
  const showDomainHint = trimmed.length > 0 && trimmed.includes('@') && !looksLikeNymr
  const canSubmit = looksLikeNymr && !mut.isPending

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    mut.mutate(trimmed)
  }

  // Friendly error text for the known server outcomes. The api wrapper
  // throws Error('${status} ${statusText}: ${body}'), and the body is the
  // JSON payload from the handler - pattern-match on the error key in there.
  const errorMessage = (() => {
    if (!mut.isError) return null
    const msg = (mut.error as Error).message
    if (msg.includes('"not-found-in-entra"')) {
      return 'No user found in Entra with that email. Check the spelling or have them sign in first.'
    }
    if (msg.includes('"invalid-email"')) {
      return 'Email must be a valid @nymr.co.uk address.'
    }
    if (msg.includes('"graph-error"')) {
      return "Couldn't reach Microsoft Entra. The User.Read.All consent may have lapsed - check with IT."
    }
    if (msg.startsWith('403')) {
      return 'You need admin access to add users.'
    }
    return "Couldn't add user - please try again."
  })()

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center p-5"
      style={{ background: 'rgba(31, 29, 24, 0.55)', backdropFilter: 'blur(4px)' }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-lg shadow-xl w-full max-w-md my-12 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl">Add user</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            className="text-ink-soft hover:text-ink text-xl disabled:opacity-50"
          >
            ×
          </button>
        </div>

        <p className="text-sm text-ink-soft mb-3">
          Pre-create a user by entering their NYMR email. They'll be looked
          up in Microsoft Entra and added to the directory so admins can set
          flags before their first sign-in.
        </p>

        <label className="block mb-3">
          <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft block mb-1">
            Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="firstname.lastname@nymr.co.uk"
            autoFocus
            required
            disabled={mut.isPending}
            className={
              'w-full border rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 transition-colors ' +
              (showDomainHint
                ? 'border-oxblood focus:border-oxblood focus:ring-oxblood/30'
                : 'border-line focus:border-green focus:ring-green/30')
            }
          />
          {showDomainHint && (
            <div className="text-xs text-oxblood mt-1">
              Email must end with @nymr.co.uk.
            </div>
          )}
        </label>

        {errorMessage && (
          <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-2 text-xs mb-3">
            {errorMessage}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-line">
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            className="px-4 py-2 rounded text-sm text-ink-soft hover:bg-parchment"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mut.isPending ? 'Looking up…' : 'Add user'}
          </button>
        </div>
      </form>
    </div>
  )
}

// ---- Row -----------------------------------------------------------------

function UserRow({ user, onClick }: { user: AdminUser; onClick: () => void }) {
  const role = deriveRole(user)
  return (
    <tr
      onClick={onClick}
      className="border-b border-line last:border-b-0 hover:bg-parchment/50 cursor-pointer transition-colors"
    >
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2.5">
          <div
            className={
              'w-8 h-8 rounded-full text-parchment text-xs font-bold flex items-center justify-center flex-shrink-0 ' +
              (user.isActive ? '' : 'opacity-50')
            }
            style={{ background: 'linear-gradient(135deg, #9b7f2a 0%, #c9a13a 100%)' }}
          >
            {user.avatarInitials ?? '?'}
          </div>
          <div className="min-w-0">
            <div className="font-semibold truncate">{user.name ?? '—'}</div>
            <div className="text-xs text-ink-soft truncate font-mono">{user.email ?? user.upn ?? ''}</div>
          </div>
          {!user.isActive && (
            <span className="ml-1 text-[10px] font-semibold uppercase tracking-wide text-oxblood">
              inactive
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5">
        <RolePill role={role} />
      </td>
      <td className="px-3 py-2.5">
        <FinancePill granted={user.hasFinancial} />
      </td>
      <td className="px-3 py-2.5 text-xs text-ink-soft font-mono">
        {user.lastSeenAt ? relativeShort(user.lastSeenAt) : <span className="italic">never</span>}
      </td>
      <td className="px-3 py-2.5 text-xs text-ink-soft font-mono">
        {user.magicLinkCount30d}
      </td>
      <td className="px-3 py-2.5 text-ink-soft">
        <Icon name="chevronRight" size={14} />
      </td>
    </tr>
  )
}

// ---- Finance pill --------------------------------------------------------

function FinancePill({ granted }: { granted: boolean }) {
  return (
    <span
      className={
        'inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ' +
        (granted ? 'text-green' : 'text-ink-soft')
      }
      style={granted ? { background: 'rgba(52,168,90,0.12)' } : { background: '#ece6db' }}
    >
      <span
        aria-hidden
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: granted ? '#0a4a3a' : '#7a7468' }}
      />
      {granted ? 'granted' : 'restricted'}
    </span>
  )
}

// ---- Detail drawer -------------------------------------------------------

function UserDetailDrawer({ personId, onClose }: { personId: number; onClose: () => void }) {
  const { data: user, isLoading, error } = useQuery({
    queryKey: ['admin-user', personId],
    queryFn: () => ApiClient.adminUser(personId)
  })

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: 'rgba(31, 29, 24, 0.45)', backdropFilter: 'blur(3px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl flex flex-col"
      >
        <div className="px-5 py-4 border-b border-line flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-xl truncate">{user?.name ?? (isLoading ? 'Loading…' : 'Unknown')}</h2>
            <div className="text-xs text-ink-soft font-mono truncate">
              {user?.email ?? user?.upn ?? ''}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-ink-soft hover:text-ink text-2xl leading-none p-1"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="m-5 bg-oxblood-soft border border-oxblood text-oxblood rounded p-3 text-sm">
            <div className="font-semibold mb-1">Could not load user</div>
            <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
          </div>
        )}

        {user && <DrawerBody user={user} />}
      </div>
    </div>
  )
}

function DrawerBody({ user }: { user: AdminUserDetail }) {
  return (
    <div className="flex-1 p-5 space-y-5">
      <Section title="Identity">
        <KV label="Display name" value={user.name ?? '—'} />
        <KV label="Email" value={user.email ?? user.upn ?? '—'} mono />
        <KV label="Department" value={user.department?.name ?? '—'} />
        <KV label="Phone" value={user.phone ?? '—'} mono />
        <KV label="Entra object id" value={user.entraObjectId ?? '—'} mono last />
      </Section>

      <AccessEditor user={user} />

      <Section title="Sign-in & magic-links">
        <KV
          label="Last sign-in"
          value={user.lastSeenAt ? relativeShort(user.lastSeenAt) : <span className="italic text-ink-soft">never</span>}
        />
        <KV label="Links issued (30d)" value={String(user.magicLinkCount30d)} last />
        <div className="mt-3">
          <MagicLinkTimeline
            records={user.magicLinks as MagicLinkRecord[]}
            // No onRevoke wired — AuthMagicLinks lacks revoked_at columns
            // (spec 44 §11 / SNAGS). MagicLinkTimeline hides the Revoke
            // button when onRevoke is undefined.
          />
        </div>
      </Section>
    </div>
  )
}

// ---- Access editor -------------------------------------------------------
//
// Editable Access section. Both fields are PATCH-able via /api/access/
// users/:id and persist across sign-ins (upsertUser excludes them from
// its MERGE SET list). The Role pill is live-reactive on the local
// hasFinancial state so the admin can see the effect of the toggle
// before they commit it.

function AccessEditor({ user }: { user: AdminUserDetail }) {
  const qc = useQueryClient()
  const [roleLabel, setRoleLabel] = useState(user.roleLabel ?? '')
  const [hasFinancial, setHasFinancial] = useState(user.hasFinancial)

  const patch = useMutation({
    mutationFn: (body: AdminUserPatch) => ApiClient.patchAdminUser(user.id, body),
    onSuccess: (updated) => {
      // Invalidate both the list (row chrome — role pill, finance pill)
      // and the detail (drawer fields). Drawer refetches and re-renders
      // with the same user prop shape; we also sync local form state to
      // the server-normalised values (empty roleLabel → null).
      qc.invalidateQueries({ queryKey: ['admin-users'] })
      qc.invalidateQueries({ queryKey: ['admin-user', user.id] })
      setRoleLabel(updated.roleLabel ?? '')
      setHasFinancial(updated.hasFinancial)
    }
  })

  // Normalise empty/whitespace input to null so the dirty check matches
  // what the server will store. Mirrors the handler's behaviour.
  const normalisedRoleLabel = roleLabel.trim() === '' ? null : roleLabel.trim()
  const roleLabelDirty    = normalisedRoleLabel !== (user.roleLabel ?? null)
  const hasFinancialDirty = hasFinancial !== user.hasFinancial
  const dirty = roleLabelDirty || hasFinancialDirty
  const saving = patch.isPending

  function handleSave(e: FormEvent) {
    e.preventDefault()
    if (!dirty || saving) return
    // Send only the fields that actually changed so the audit log
    // reflects user intent (if the admin only toggled one, only that
    // shows in the audit diff).
    const body: AdminUserPatch = {}
    if (roleLabelDirty)    body.roleLabel = normalisedRoleLabel
    if (hasFinancialDirty) body.hasFinancial = hasFinancial
    patch.mutate(body)
  }

  function handleReset() {
    setRoleLabel(user.roleLabel ?? '')
    setHasFinancial(user.hasFinancial)
    patch.reset()  // clears any pending error state
  }

  // Live-reactive role pill — uses the *local* hasFinancial so the admin
  // can preview the effect of their toggle before saving.
  const previewRole = deriveRole({ ...user, hasFinancial })

  return (
    <section className="bg-parchment/40 border border-line rounded p-4">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-ink-soft mb-2">
        Access
      </div>

      <form onSubmit={handleSave}>
        <KV label="Role" value={<RolePill role={previewRole} />} />

        <KV label="Role label" value={
          <input
            type="text"
            value={roleLabel}
            onChange={(e) => setRoleLabel(e.target.value)}
            maxLength={100}
            placeholder="(none)"
            disabled={saving}
            className="w-full border border-line rounded px-2 py-1 text-sm bg-white focus:outline-none focus:border-green focus:ring-1 focus:ring-green/30 disabled:opacity-50"
          />
        } />

        <KV label="Financial access" value={
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hasFinancial}
              onChange={(e) => setHasFinancial(e.target.checked)}
              disabled={saving}
              className="w-4 h-4 accent-green cursor-pointer disabled:cursor-not-allowed"
            />
            <FinancePill granted={hasFinancial} />
          </label>
        } />

        <KV label="Account status" value={
          user.isActive
            ? <span className="text-green font-semibold">Active</span>
            : <span className="text-oxblood font-semibold">Inactive</span>
        } last />

        {patch.isError && (
          <div className="mt-3 bg-oxblood-soft border border-oxblood text-oxblood rounded p-2 text-xs">
            Save failed: {(patch.error as Error).message}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-line/60">
          <button
            type="button"
            onClick={handleReset}
            disabled={!dirty || saving}
            className="px-3 py-1.5 rounded text-xs font-semibold text-ink-soft hover:bg-parchment disabled:opacity-40 disabled:hover:bg-transparent"
          >
            Reset
          </button>
          <button
            type="submit"
            disabled={!dirty || saving}
            className="px-3 py-1.5 rounded text-xs font-semibold bg-green text-parchment hover:bg-green/90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>

        <p className="text-xs text-ink-soft mt-3 italic">
          Role label and financial access are admin-managed and persist
          across sign-ins. Admin / volunteer status is sourced from Entra
          group membership (SG-Events-Admins, SG-Events-Volunteers); the
          5-tier role pill above is derived from those flags plus this
          financial toggle.
        </p>
      </form>
    </section>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-parchment/40 border border-line rounded p-4">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-ink-soft mb-2">
        {title}
      </div>
      {children}
    </section>
  )
}

function KV({
  label, value, mono, last
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
  last?: boolean
}) {
  return (
    <div
      className="grid items-center gap-3 py-1.5"
      style={{
        gridTemplateColumns: '140px 1fr',
        borderBottom: last ? 'none' : '1px solid #ece6db'
      }}
    >
      <div className="text-[10px] uppercase tracking-wider font-semibold text-ink-soft">
        {label}
      </div>
      <div className={'text-sm ' + (mono ? 'font-mono text-xs' : 'font-semibold')}>
        {value}
      </div>
    </div>
  )
}

// ---- Helpers -------------------------------------------------------------

// Derive the spec-44 5-tier role from the legacy flags on dbo.Users.
// Order matters: admin wins over finance wins over volunteer; everything
// else defaults to 'editor' (an in-app user with no special role). The
// 'manager' slot in the enum is reserved for the future migration that
// adds a discrete role column; for now no one resolves to manager.
function deriveRole(u: AdminUser): UserRole {
  if (u.isAdmin) return 'admin'
  if (u.hasFinancial) return 'finance'
  if (u.isVolunteer) return 'volunteer'
  return 'editor'
}

// Short relative time for the table column ("2m / 3h / 5d / 2w / 3mo").
function relativeShort(iso: string): string {
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return '—'
  const secs = Math.max(0, Math.floor((Date.now() - t) / 1000))
  if (secs < 60) return 'just now'
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d`
  const weeks = Math.floor(days / 7)
  if (weeks < 5) return `${weeks}w`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo`
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
